import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Star, Quote } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import GoogleBadge from "@/components/GoogleBadge";
import api from "@/lib/api";
import { CTASection } from "@/pages/Home";

export default function Testimonials({ lang }) {
  const { t } = useTranslation();
  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => (await api.get("/testimonials")).data,
  });
  const items = testimonials || [];

  return (
    <>
      <section data-testid="testimonials-hero" className="pt-40 pb-16 md:pt-52 md:pb-24 px-6 md:px-12 bg-navy text-offwhite">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Kicker light>{t("testiPage.kicker")}</Kicker>
            <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
              {t("testiPage.title")}
            </h1>
            <p className="mt-6 text-offwhite/60 max-w-xl">{t("testiPage.sub")}</p>
            <div className="mt-8">
              <GoogleBadge dark />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="testimonials-list" className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? [0, 1, 2].map((i) => <div key={i} className="h-64 bg-offwhite-dark animate-pulse" />)
            : items.map((item, i) => (
                <Reveal key={item.id} delay={(i % 3) * 0.1}>
                  <div data-testid={`testimonial-card-${i}`} className="h-full bg-white border border-sandline p-8 flex flex-col justify-between hover:border-orange/40 transition-colors duration-300">
                    <div>
                      <Quote size={24} className="text-orange mb-4" fill="currentColor" strokeWidth={0} />
                      <div className="flex gap-1 mb-4">
                        {Array.from({ length: item.rating || 5 }).map((_, j) => (
                          <Star key={j} size={14} className="text-orange" fill="currentColor" />
                        ))}
                      </div>
                      <blockquote className="text-navy/90 leading-relaxed">“{item.text}”</blockquote>
                    </div>
                    <div className="mt-8 flex items-center gap-3">
                      <span className="w-9 h-9 flex items-center justify-center bg-navy text-offwhite font-heading font-bold text-sm">{item.name.charAt(0)}</span>
                      <div>
                        <p className="font-bold text-sm">{item.name}</p>
                        <p className="text-xs text-slateblue">{item.location}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
        </div>
      </section>

      <CTASection lang={lang} />
    </>
  );
}
